/**
 * The preview gizmo (W2-02; DESIGN.md §5.6.6, U§5 B1): direct manipulation of the active line.
 *
 *  - Handles: the centre moves the line (Position), the ring rotates it (Shift: 15-degree steps), the two end handles
 *    change its Length (normal lines) or Size (image, GIF, text and other lines; Shift: width only). Dragging the
 *    active line's body also moves it; a click on it without dragging cycles like any line click.
 *  - Writes follow the key rule (gizmoKeys.ts) with Position as a pair, Shift axis lock, Ctrl = "Offset whole
 *    motion", auto-key, and go to the active layer (Full) or layer 0 (Guided). The rule is stated per axis in the
 *    hint bar during the drag ("X: new keyframe at 12:0 · Y: whole chart").
 *  - When a keyframe will be written, the playhead visibly snaps to the beat grid at gesture start (paused), so the
 *    line does not jump on release.
 *  - Child lines are dragged in their parent's frame (gizmoMath.worldDeltaToMove).
 *  - Snapping (toolbar): canvas grid, screen centre / edges, other lines' anchors; Alt drags freely.
 *  - Everything runs as one DocStore gesture: live in the preview, timeline and inspector, one undo step on release,
 *    Esc cancels. Locked lines (Lines panel) show the gizmo greyed and refuse edits.
 * `PropertyEdit` is the same machinery for the opacity slider under the readout.
 */

import type { AppServices } from '../../../app/registry/types';
import type { LineStateRt } from '../../../core/engine/types';
import { kindOfLine, isUiMover } from '../../../core/model/lineKind';
import { Defaults } from '../../../core/model/defaults';
import type { LineId, LineProp } from '../../../core/model/types';
import { Beats } from '../../../core/time/beat';
import { formatBeat } from '../../../core/time/beatSyntax';
import type { Beat, Sec } from '../../../core/time/types';
import type { MessageRef, Unsubscribe, Vec2 } from '../../../core/util/types';
import type {
  OverlayDrawContext,
  OverlayHit,
  OverlayPointerEvent,
  PreviewOverlayProvider,
} from '../../../render/types';
import { PREVIEW_FRAME_ID } from '../../../render/types';
import type { Gesture, TxnOutcome } from '../../../state/types';
import { filteredOut, previewFiltersOf } from '../filters';
import { m } from '../preview.messages';
import { EXT_SNAP, readExt, snapPrefsOf } from '../prefs';
import { applyKeyPlan, layerFor, offsetValues, planKeyWrites, writesAtPlayhead, type PropPlan } from './gizmoKeys';
import {
  lengthFactor,
  lockAxisOf,
  rotateStep,
  rpePerPx,
  rpeToWorld,
  screenAngleDeg,
  snapPoint,
  snapToStep,
  tidyValue,
  unwrapAngle,
  worldDeltaToMove,
  worldToRpe,
  type Axis,
  type SnapResult,
} from './gizmoMath';
import { lineStateCopy } from './context';
import { clickAt } from './selection';
import { dp, drawLabel, frameInfo, lineShape, shapeDistance, PICK_TOLERANCE } from './shared';
import { overlayTheme } from './theme';

/** Gizmo sizes in CSS px. */
export const CENTRE_RADIUS = 11;
export const RING_RADIUS = 46;
export const HANDLE_DISTANCE = 82;
export const HANDLE_RADIUS = 9;
/** Pointer travel (CSS px) before a body drag starts and before a Shift lock picks its axis. */
const DRAG_START = 3;
const LOCK_DECIDE = 4;
/** Snap distance in CSS px. */
const SNAP_PX = 8;

export type GizmoHandle = 'move' | 'moveBody' | 'rotate' | 'lengthA' | 'lengthB' | 'locked';

export interface GizmoGeometry {
  readonly centre: Vec2;
  /** Unit vector of the line's +x on screen (device px). */
  readonly dir: Vec2;
  readonly ring: number;
  readonly a: Vec2;
  readonly b: Vec2;
}

/** Gizmo geometry of a line state in device px. */
export function gizmoGeometry(c: OverlayDrawContext, s: LineStateRt): GizmoGeometry {
  const centre = c.worldToPx(s.x, s.y);
  const r = (s.rot * Math.PI) / 180;
  // World y is up, screen y is down.
  const dir: Vec2 = [Math.cos(r), -Math.sin(r)];
  const d = dp(c, HANDLE_DISTANCE);
  return {
    centre,
    dir,
    ring: dp(c, RING_RADIUS),
    a: [centre[0] - dir[0] * d, centre[1] - dir[1] * d],
    b: [centre[0] + dir[0] * d, centre[1] + dir[1] * d],
  };
}

/** Which handle a point (device px) is on, or null. */
export function gizmoHandleAt(
  c: OverlayDrawContext,
  g: GizmoGeometry,
  px: number,
  py: number,
): Exclude<GizmoHandle, 'moveBody' | 'locked'> | null {
  const dc = Math.hypot(px - g.centre[0], py - g.centre[1]);
  if (dc <= dp(c, CENTRE_RADIUS)) return 'move';
  if (Math.hypot(px - g.a[0], py - g.a[1]) <= dp(c, HANDLE_RADIUS)) return 'lengthA';
  if (Math.hypot(px - g.b[0], py - g.b[1]) <= dp(c, HANDLE_RADIUS)) return 'lengthB';
  if (Math.abs(dc - g.ring) <= dp(c, PICK_TOLERANCE)) return 'rotate';
  return null;
}

/** Properties a handle writes for a line kind. */
export function propsForHandle(handle: GizmoHandle, normalLine: boolean): readonly LineProp[] {
  switch (handle) {
    case 'rotate':
      return ['rotate'];
    case 'lengthA':
    case 'lengthB':
      return normalLine ? ['scaleX'] : ['scaleX', 'scaleY'];
    default:
      return ['moveX', 'moveY'];
  }
}

// ---------------------------------------------------------------------------------------------------------------
// PropertyEdit: a live, rule-following edit of some properties of one line
// ---------------------------------------------------------------------------------------------------------------

export interface EditVariant {
  readonly offset: boolean;
}

/** Value of a property where its written layer has no track. */
function absentValue(services: AppServices, line: LineId, prop: LineProp, layer: number): number {
  const store = services.store;
  const rec = store?.doc.lines.get(line);
  if (store === null || rec === undefined) return 0;
  const kind = kindOfLine(store.doc, store.idx, rec);
  return Defaults.leadInValue(prop, layer, kind, isUiMover(rec)) ?? 1;
}

/** Number of keys of a track that the readout and hints mention. */
function propTitleKey(prop: LineProp, normalLine: boolean): string {
  switch (prop) {
    case 'moveX':
      return m.propX;
    case 'moveY':
      return m.propY;
    case 'rotate':
      return m.propRotate;
    case 'alpha':
      return m.propOpacity;
    case 'scaleX':
      return normalLine ? m.propLength : m.propWidth;
    case 'scaleY':
      return m.propHeight;
    default:
      return m.propOther;
  }
}

export class PropertyEdit {
  readonly line: LineId;
  readonly props: readonly LineProp[];
  /** Start value of each property on its written layer at the edit time. */
  readonly starts: ReadonlyMap<LineProp, number>;
  /** Beat keys are written at (the playhead, snapped). */
  readonly at: Beat;
  readonly atSec: Sec;
  readonly normalLine: boolean;
  private readonly services: AppServices;
  private readonly plans: ReadonlyMap<boolean, readonly PropPlan[]>;
  private readonly gesture: Gesture;
  private last: TxnOutcome<void> | null = null;
  private done = false;

  private constructor(
    services: AppServices,
    line: LineId,
    props: readonly LineProp[],
    at: Beat,
    atSec: Sec,
    starts: ReadonlyMap<LineProp, number>,
    plans: ReadonlyMap<boolean, readonly PropPlan[]>,
    gesture: Gesture,
    normalLine: boolean,
  ) {
    this.services = services;
    this.line = line;
    this.props = props;
    this.at = at;
    this.atSec = atSec;
    this.starts = starts;
    this.plans = plans;
    this.gesture = gesture;
    this.normalLine = normalLine;
  }

  /**
   * Starts an edit of `props` of `line` (null when no project / engine, or the line is locked: a toast says why).
   * `offset` is the plan used for the playhead snap decision (Ctrl held at the start).
   */
  static begin(
    services: AppServices,
    line: LineId,
    props: readonly LineProp[],
    label: string,
    offset: boolean,
  ): PropertyEdit | null {
    const { store, engine } = services;
    if (store === null || engine === null) return null;
    const rec = store.doc.lines.get(line);
    if (rec === undefined) return null;
    const st = services.editor.getState();
    if (st.filters.lockedLines.has(line)) {
      services.ui.toast({ key: m.lineLocked }, { kind: 'warning' });
      return null;
    }
    const layer = st.level === 'guided' ? 0 : st.activeLayer;
    const transport = services.audio.transport;
    const tempo = engine.tempo;
    const t0 = transport.chartTime();
    const exact = tempo.beatAt(Math.max(0, t0), 1920);
    const at = st.snap.beatSnap ? Beats.snap(exact, st.snap.div) : exact;
    const atBeat = Beats.lt(at, Beats.ZERO) ? Beats.ZERO : at;
    const input = {
      line,
      props,
      layer,
      at: atBeat,
      autoKey: st.preview.autoKey,
      lock: null,
      selectedKeys: st.selection.keys,
    } as const;
    const plans = new Map<boolean, readonly PropPlan[]>([
      [false, planKeyWrites(store.doc, store.idx, { ...input, offset: false })],
      [true, planKeyWrites(store.doc, store.idx, { ...input, offset: true })],
    ]);
    // When a keyframe will be written, the playhead visibly snaps to the grid (paused) and the start values are read
    // at the key's time, so the line does not jump on release. Whole-chart edits read the values where the playhead is.
    const keyed = writesAtPlayhead(plans.get(offset) ?? []);
    const snappedSec = tempo.secAt(atBeat);
    if (keyed && !transport.state.playing && Math.abs(snappedSec - t0) > 1e-9) transport.seek(snappedSec);
    const atSec = keyed ? snappedSec : t0;
    const starts = new Map<LineProp, number>();
    for (const p of props) {
      const l = layerFor(p, layer);
      const v = engine.channelValue(line, p, atSec, l);
      starts.set(p, Number.isFinite(v) ? v : absentValue(services, line, p, l));
    }
    const normalLine = kindOfLine(store.doc, store.idx, rec) === 'normal' && !isUiMover(rec);
    const gesture = store.beginGesture({ label: { key: label }, command: 'preview.gizmo' });
    return new PropertyEdit(services, line, props, atBeat, atSec, starts, plans, gesture, normalLine);
  }

  plan(v: EditVariant): readonly PropPlan[] {
    return this.plans.get(v.offset) ?? [];
  }

  /**
   * Applies target values for the properties given: absolute values, or for an offset (Ctrl) the DELTA of every
   * property (a property without a track is then created at its start value plus the delta, `offsetValues`).
   */
  update(values: ReadonlyMap<LineProp, number>, v: EditVariant): TxnOutcome<void> {
    const plan = this.plan(v);
    const resolved = v.offset ? offsetValues(plan, values, this.starts) : values;
    this.last = this.gesture.update((tx) => {
      applyKeyPlan(tx, this.line, plan, resolved);
    });
    return this.last;
  }

  /** Commits the gesture (one undo step); returns the failure reason when the last update failed. */
  commit(): MessageRef | null {
    if (this.done) return null;
    this.done = true;
    if (this.last !== null && !this.last.ok) {
      this.gesture.cancel();
      return this.last.reason;
    }
    if (this.last === null) {
      this.gesture.cancel();
      return null;
    }
    this.gesture.commit();
    return null;
  }

  cancel(): void {
    if (this.done) return;
    this.done = true;
    this.gesture.cancel();
  }

  /** The key rule per property, for the hint bar ("X: new keyframe at 12:0", "Y: whole chart"). */
  ruleHints(v: EditVariant, written: ReadonlySet<LineProp>): MessageRef[] {
    return ruleRefs(this.services, this.plan(v), this.at, this.normalLine, written);
  }
}

/** The key rule of planned writes as messages, one per written property ("X: new keyframe at 12:0"). */
export function ruleRefs(
  services: AppServices,
  plans: readonly PropPlan[],
  at: Beat,
  normalLine: boolean,
  written: ReadonlySet<LineProp>,
): MessageRef[] {
  const beat = formatBeat(at, 'colon');
  const out: MessageRef[] = [];
  for (const p of plans) {
    if (!written.has(p.prop)) continue;
    const axis = services.i18n.t(propTitleKey(p.prop, normalLine));
    const a = p.action;
    switch (a.kind) {
      case 'key':
        out.push({ key: a.existing === null ? m.ruleKeyNew : m.ruleKeyUpdate, params: { axis, beat } });
        break;
      case 'create':
        out.push(
          Beats.eq(a.at, Beats.ZERO)
            ? { key: m.ruleWholeNew, params: { axis } }
            : { key: m.ruleCreateAt, params: { axis, beat } },
        );
        break;
      case 'whole':
        out.push({ key: m.ruleWhole, params: { axis } });
        break;
      case 'offset':
        out.push({
          key: a.selectedOnly ? m.ruleOffsetSelected : m.ruleOffset,
          params: { axis, count: a.keys.length },
        });
        break;
      case 'skip':
        break;
    }
  }
  return out;
}

/**
 * The key rule a drag of `props` would follow now (hover tooltip of the gizmo handles), as one sentence:
 * "X: new keyframe at 12:0 · Y: whole chart". Null without a project.
 */
export function previewRule(
  services: AppServices,
  line: LineId,
  props: readonly LineProp[],
  normalLine: boolean,
): MessageRef | null {
  const { store, engine } = services;
  if (store === null || engine === null) return null;
  const st = services.editor.getState();
  const exact = engine.tempo.beatAt(Math.max(0, services.audio.transport.chartTime()), 1920);
  const at = st.snap.beatSnap ? Beats.snap(exact, st.snap.div) : exact;
  const plans = planKeyWrites(store.doc, store.idx, {
    line,
    props,
    layer: st.level === 'guided' ? 0 : st.activeLayer,
    at: Beats.lt(at, Beats.ZERO) ? Beats.ZERO : at,
    autoKey: st.preview.autoKey,
    offset: false,
    lock: null,
    selectedKeys: st.selection.keys,
  });
  const parts = ruleRefs(services, plans, at, normalLine, new Set(props)).map((r) => services.i18n.tr(r));
  if (parts.length === 0) return null;
  return { key: m.ruleTooltip, params: { rules: parts.join(' \u00b7 ') } };
}

// ---------------------------------------------------------------------------------------------------------------
// The provider
// ---------------------------------------------------------------------------------------------------------------

interface Drag {
  readonly handle: Exclude<GizmoHandle, 'locked'>;
  readonly line: LineId;
  readonly startPx: Vec2;
  readonly startWorld: Vec2;
  /** The line's composed position and its parent's rotation at the time keys are written. */
  lineWorld: Vec2;
  parentRot: number;
  readonly ar: number;
  readonly centrePx: Vec2;
  readonly dir: Vec2;
  /** RPE positions of the other lines' anchors (snapping). */
  readonly anchors: ReadonlyArray<readonly [number, number]>;
  edit: PropertyEdit | null;
  lastAngle: number;
  angle: number;
  lock: Axis | null;
  moved: boolean;
  snap: SnapResult | null;
  pointer: Vec2;
  summary: string;
  hintsKey: string;
  offEsc: Unsubscribe | null;
}

function labelFor(handle: GizmoHandle, normalLine: boolean, offset: boolean): string {
  if (offset) return m.txnOffset;
  switch (handle) {
    case 'rotate':
      return m.txnRotate;
    case 'lengthA':
    case 'lengthB':
      return normalLine ? m.txnLength : m.txnSize;
    default:
      return m.txnMove;
  }
}

function fmt(v: number): string {
  return String(tidyValue(v, 2));
}

export function createGizmoOverlay(services: AppServices): PreviewOverlayProvider {
  let drag: Drag | null = null;

  const activeState = (c: OverlayDrawContext): LineStateRt | null => {
    const st = services.editor.getState();
    if (!st.preview.gizmo || st.activeLine === null) return null;
    if (filteredOut(st.filters, st.activeLine)) return null;
    return frameInfo(c).states.get(st.activeLine) ?? null;
  };

  const isNormal = (line: LineId): boolean => {
    const store = services.store;
    const rec = store?.doc.lines.get(line);
    return store !== null && rec !== undefined && kindOfLine(store.doc, store.idx, rec) === 'normal' && !isUiMover(rec);
  };

  const setHints = (d: Drag, e: OverlayPointerEvent): void => {
    if (d.edit === null) return;
    const variant = { offset: e.ctrl };
    const written = new Set<LineProp>(writtenProps(d, e));
    const rules = d.edit.ruleHints(variant, written);
    const shiftLabel =
      d.handle === 'rotate'
        ? m.hint15
        : d.handle === 'lengthA' || d.handle === 'lengthB'
          ? m.hintWidthOnly
          : m.hintLock;
    const items: Array<{ chord: string; label: MessageRef }> = rules.map((label) => ({ chord: '', label }));
    if (d.handle !== 'lengthA' && d.handle !== 'lengthB') items.push({ chord: 'Shift', label: { key: shiftLabel } });
    else if (!d.edit.normalLine) items.push({ chord: 'Shift', label: { key: shiftLabel } });
    items.push({ chord: 'Ctrl', label: { key: m.hintOffset } });
    if (d.handle === 'move' || d.handle === 'moveBody' || d.handle === 'rotate')
      items.push({ chord: 'Alt', label: { key: m.hintNoSnap } });
    items.push({ chord: 'Escape', label: { key: m.hintCancel } });
    const key = JSON.stringify(items);
    if (key === d.hintsKey) return;
    d.hintsKey = key;
    services.ui.setGestureHints(items);
  };

  /** Properties written by the current pointer state (Shift lock / width only). */
  const writtenProps = (d: Drag, e: OverlayPointerEvent): LineProp[] => {
    if (d.edit === null) return [];
    if (d.handle === 'move' || d.handle === 'moveBody') {
      if (e.shift && d.lock !== null) return [d.lock === 'x' ? 'moveX' : 'moveY'];
      return ['moveX', 'moveY'];
    }
    if ((d.handle === 'lengthA' || d.handle === 'lengthB') && e.shift && !d.edit.normalLine) return ['scaleX'];
    return [...d.edit.props];
  };

  const finish = (): void => {
    const d = drag;
    drag = null;
    d?.offEsc?.();
    services.ui.setGestureHints(null);
    services.scheduler.request(PREVIEW_FRAME_ID);
  };

  const cancel = (): void => {
    drag?.edit?.cancel();
    finish();
  };

  const listenEsc = (): Unsubscribe => {
    if (typeof window === 'undefined') return () => undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || drag === null) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      cancel();
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
    };
  };

  const begin = (c: OverlayDrawContext, d: Drag, e: OverlayPointerEvent): boolean => {
    const normal = isNormal(d.line);
    const props = propsForHandle(d.handle, normal);
    const edit = PropertyEdit.begin(services, d.line, props, labelFor(d.handle, normal, e.ctrl), e.ctrl);
    if (edit === null) return false;
    d.edit = edit;
    // The playhead may have snapped to the grid: take the line's pose at the time the keys are written.
    const engine = services.engine;
    if (engine !== null && Math.abs(edit.atSec - c.t) > 1e-9) {
      const st = services.editor.getState();
      const filters = previewFiltersOf(st.filters, st.activeLine);
      const s = lineStateCopy(engine, d.line, edit.atSec, filters, d.ar);
      d.lineWorld = [s.x, s.y];
      const parent = services.store?.doc.lines.get(d.line)?.parent ?? null;
      d.parentRot = parent === null ? 0 : lineStateCopy(engine, parent, edit.atSec, filters, d.ar).rot;
    }
    d.offEsc = listenEsc();
    setHints(d, e);
    return true;
  };

  const values = (c: OverlayDrawContext, d: Drag, e: OverlayPointerEvent): Map<LineProp, number> => {
    const edit = d.edit;
    const out = new Map<LineProp, number>();
    if (edit === null) return out;
    const offset = e.ctrl;
    const start = (p: LineProp): number => edit.starts.get(p) ?? 0;
    if (d.handle === 'move' || d.handle === 'moveBody') {
      const [wx, wy] = c.pxToWorld(e.px, e.py);
      let tx = d.lineWorld[0] + wx - d.startWorld[0];
      let ty = d.lineWorld[1] + wy - d.startWorld[1];
      d.snap = null;
      if (!e.alt) {
        const prefs = readExt(services.settings, EXT_SNAP, snapPrefsOf);
        const [rx, ry] = rpePerPx(c.viewport.W, d.ar);
        const thr = dp(c, SNAP_PX);
        const [X, Y] = worldToRpe(tx, ty, d.ar);
        const r = snapPoint(X, Y, {
          grid: prefs.grid ? prefs.gridSize : 0,
          centre: prefs.centre,
          edges: prefs.edges,
          anchors: prefs.anchors ? d.anchors : [],
          thresholdX: thr * rx,
          thresholdY: thr * ry,
        });
        d.snap = r;
        [tx, ty] = rpeToWorld(r.x, r.y, d.ar);
      }
      const [dX, dY] = worldDeltaToMove(tx - d.lineWorld[0], ty - d.lineWorld[1], d.parentRot, d.ar);
      if (e.shift) {
        const travel = Math.hypot(e.px - d.startPx[0], e.py - d.startPx[1]);
        // Decided on the pointer's own travel in world units (isotropic), not on the snapped RPE delta.
        if (d.lock === null && travel >= dp(c, LOCK_DECIDE))
          d.lock = lockAxisOf(wx - d.startWorld[0], wy - d.startWorld[1], d.parentRot);
      } else d.lock = null;
      const lockX = e.shift && d.lock === 'y';
      const lockY = e.shift && d.lock === 'x';
      if (!lockX) out.set('moveX', offset ? tidyValue(dX) : tidyValue(start('moveX') + dX));
      if (!lockY) out.set('moveY', offset ? tidyValue(dY) : tidyValue(start('moveY') + dY));
      const parts: string[] = [];
      if (!lockX)
        parts.push(services.i18n.t(m.summaryAxis, { axis: services.i18n.t(m.propX), value: fmt(start('moveX') + dX) }));
      if (!lockY)
        parts.push(services.i18n.t(m.summaryAxis, { axis: services.i18n.t(m.propY), value: fmt(start('moveY') + dY) }));
      d.summary = parts.join('  ');
      return out;
    }
    if (d.handle === 'rotate') {
      const a = screenAngleDeg(d.centrePx[0], d.centrePx[1], e.px, e.py);
      d.angle = unwrapAngle(d.lastAngle, a);
      d.lastAngle = d.angle;
      const startAngle = screenAngleDeg(d.centrePx[0], d.centrePx[1], d.startPx[0], d.startPx[1]);
      // Whole degrees by default, 15-degree steps with Shift, free with Alt (UX-063).
      const v = tidyValue(snapToStep(start('rotate') + (d.angle - startAngle), rotateStep(e)), 2);
      out.set('rotate', offset ? tidyValue(v - start('rotate'), 2) : v);
      d.summary = services.i18n.t(m.summaryRotate, { value: fmt(v) });
      return out;
    }
    // Length / size.
    const f = lengthFactor(d.centrePx[0], d.centrePx[1], d.dir[0], d.dir[1], d.startPx, [e.px, e.py]);
    const sx = tidyValue(start('scaleX') * f);
    out.set('scaleX', offset ? tidyValue(sx - start('scaleX')) : sx);
    const widthOnly = e.shift && !edit.normalLine;
    if (edit.props.includes('scaleY') && !widthOnly) {
      const sy = tidyValue(start('scaleY') * f);
      out.set('scaleY', offset ? tidyValue(sy - start('scaleY')) : sy);
    }
    d.summary = services.i18n.t(m.summaryScale, { value: String(Math.round(f * 100)) });
    return out;
  };

  const apply = (c: OverlayDrawContext, d: Drag, e: OverlayPointerEvent): void => {
    if (d.edit === null) return;
    const vals = values(c, d, e);
    setHints(d, e);
    d.edit.update(vals, { offset: e.ctrl });
    services.scheduler.request(PREVIEW_FRAME_ID);
  };

  const drawGuides = (c: OverlayDrawContext, d: Drag): void => {
    const snap = d.snap;
    if (snap === null || (snap.snappedX === null && snap.snappedY === null)) return;
    const theme = overlayTheme();
    const ctx = c.ctx;
    const [wx, wy] = rpeToWorld(snap.x, snap.y, d.ar);
    const [px, py] = c.worldToPx(wx, wy);
    const vp = c.viewport;
    ctx.save();
    ctx.strokeStyle = theme.guide;
    ctx.lineWidth = dp(c, 1);
    ctx.setLineDash([dp(c, 4), dp(c, 4)]);
    if (snap.snappedX !== null) {
      ctx.beginPath();
      ctx.moveTo(px, vp.vy);
      ctx.lineTo(px, vp.vy + vp.H);
      ctx.stroke();
    }
    if (snap.snappedY !== null) {
      ctx.beginPath();
      ctx.moveTo(vp.vx, py);
      ctx.lineTo(vp.vx + vp.W, py);
      ctx.stroke();
    }
    ctx.restore();
  };

  const drawOffscreen = (c: OverlayDrawContext, g: GizmoGeometry, label: string): void => {
    const W = c.ctx.canvas.width;
    const H = c.ctx.canvas.height;
    const [x, y] = g.centre;
    if (x >= 0 && x <= W && y >= 0 && y <= H) return;
    const theme = overlayTheme();
    const m0 = dp(c, 14);
    const cx = Math.min(Math.max(x, m0), W - m0);
    const cy = Math.min(Math.max(y, m0), H - m0);
    const ang = Math.atan2(y - cy, x - cx);
    const ctx = c.ctx;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(ang);
    ctx.fillStyle = theme.accent;
    ctx.beginPath();
    ctx.moveTo(dp(c, 10), 0);
    ctx.lineTo(-dp(c, 6), -dp(c, 7));
    ctx.lineTo(-dp(c, 6), dp(c, 7));
    ctx.closePath();
    ctx.fill();
    ctx.restore();
    drawLabel(c, label, cx + dp(c, 10), cy + dp(c, 6), {
      fg: theme.text,
      bg: theme.surface,
      border: theme.accent,
      size: 10,
    });
  };

  return {
    id: 'preview.gizmo',
    order: 90,
    draw(c) {
      const s = activeState(c);
      if (s === null) return;
      const theme = overlayTheme();
      const locked = services.editor.getState().filters.lockedLines.has(s.line);
      const color = locked ? theme.textMuted : theme.accent;
      const g = gizmoGeometry(c, s);
      const ctx = c.ctx;
      const [cx, cy] = g.centre;
      ctx.save();
      // Rotate ring with a knob on the line direction.
      ctx.strokeStyle = color;
      ctx.lineWidth = dp(c, 1.5);
      ctx.setLineDash([dp(c, 3), dp(c, 3)]);
      ctx.beginPath();
      ctx.arc(cx, cy, g.ring, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(cx + g.dir[0] * g.ring, cy + g.dir[1] * g.ring, dp(c, 3.5), 0, Math.PI * 2);
      ctx.fill();
      // Axis between the length handles.
      ctx.lineWidth = dp(c, 1);
      ctx.beginPath();
      ctx.moveTo(g.a[0], g.a[1]);
      ctx.lineTo(g.b[0], g.b[1]);
      ctx.stroke();
      // Length / size handles (diamonds).
      const h = dp(c, HANDLE_RADIUS * 0.75);
      for (const [hx, hy] of [g.a, g.b]) {
        ctx.beginPath();
        ctx.moveTo(hx, hy - h);
        ctx.lineTo(hx + h, hy);
        ctx.lineTo(hx, hy + h);
        ctx.lineTo(hx - h, hy);
        ctx.closePath();
        ctx.fillStyle = theme.surface;
        ctx.fill();
        ctx.stroke();
      }
      // Centre: circle with a plus.
      const r = dp(c, CENTRE_RADIUS * 0.75);
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = theme.surface;
      ctx.fill();
      ctx.lineWidth = dp(c, 1.5);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx - r * 0.55, cy);
      ctx.lineTo(cx + r * 0.55, cy);
      ctx.moveTo(cx, cy - r * 0.55);
      ctx.lineTo(cx, cy + r * 0.55);
      ctx.stroke();
      ctx.restore();
      const name = services.store?.doc.lines.get(s.line)?.name ?? '';
      drawOffscreen(c, g, services.i18n.t(m.offscreen, { label: `#${String(s.index)} ${name}`.trim() }));
      const d = drag;
      if (d?.edit != null && d.moved) {
        drawGuides(c, d);
        drawLabel(c, d.summary, d.pointer[0] + dp(c, 14), d.pointer[1] + dp(c, 14), {
          fg: theme.text,
          bg: theme.surface,
          border: theme.accent,
        });
      }
    },
    hitTest(c, px, py): OverlayHit | null {
      const s = activeState(c);
      if (s === null) return null;
      const locked = services.editor.getState().filters.lockedLines.has(s.line);
      const g = gizmoGeometry(c, s);
      const handle = gizmoHandleAt(c, g, px, py);
      if (handle !== null) {
        if (locked)
          return {
            provider: 'preview.gizmo',
            cursor: 'not-allowed',
            data: { handle: 'locked', tooltip: { key: m.lineLocked } },
          };
        const cursor = handle === 'move' ? 'move' : handle === 'rotate' ? 'grab' : 'ew-resize';
        const normal = isNormal(s.line);
        const tooltip = previewRule(services, s.line, propsForHandle(handle, normal), normal);
        return { provider: 'preview.gizmo', cursor, data: tooltip === null ? { handle } : { handle, tooltip } };
      }
      // The active line's body (when it is not hidden by the drag handles).
      const store = services.store;
      const shape = lineShape(c, s, store?.doc ?? null, store?.idx ?? null);
      if (shapeDistance(shape, px, py) <= dp(c, PICK_TOLERANCE) && !locked) {
        return { provider: 'preview.gizmo', cursor: 'move', data: { handle: 'moveBody' } };
      }
      return null;
    },
    onPointerDown(c, hit, e) {
      if ((e.buttons & 1) === 0) return;
      const handle = (hit.data as { readonly handle?: GizmoHandle } | null)?.handle;
      const s = activeState(c);
      if (handle === undefined || s === null) return;
      if (handle === 'locked') {
        services.ui.toast({ key: m.lineLocked }, { kind: 'warning' });
        return;
      }
      const store = services.store;
      const rec = store?.doc.lines.get(s.line);
      const states = frameInfo(c).states;
      const parent = rec?.parent == null ? undefined : states.get(rec.parent);
      const g = gizmoGeometry(c, s);
      const ar = c.viewport.ar;
      // Snap targets: the other lines' anchors, except the dragged line's descendants (they move with it).
      const moving = new Set<LineId>([s.line]);
      if (store !== null) {
        const queue: LineId[] = [s.line];
        for (let id = queue.pop(); id !== undefined; id = queue.pop()) {
          for (const child of store.idx.childrenOf(id)) {
            if (moving.has(child)) continue;
            moving.add(child);
            queue.push(child);
          }
        }
      }
      const anchors: Array<readonly [number, number]> = [];
      for (const o of c.lines) {
        if (moving.has(o.line)) continue;
        anchors.push(worldToRpe(o.x, o.y, ar));
      }
      const startAngle = screenAngleDeg(g.centre[0], g.centre[1], e.px, e.py);
      const d: Drag = {
        handle,
        line: s.line,
        startPx: [e.px, e.py],
        startWorld: e.world,
        lineWorld: [s.x, s.y],
        parentRot: parent?.rot ?? 0,
        ar,
        centrePx: g.centre,
        dir: g.dir,
        anchors,
        edit: null,
        lastAngle: startAngle,
        angle: startAngle,
        lock: null,
        moved: false,
        snap: null,
        pointer: [e.px, e.py],
        summary: '',
        hintsKey: '',
        offEsc: null,
      };
      drag = d;
      if (handle !== 'moveBody') {
        if (!begin(c, d, e)) {
          drag = null;
          return;
        }
        d.moved = true;
      }
    },
    onPointerMove(c, e) {
      const d = drag;
      if (d === null) return;
      if ((e.buttons & 1) === 0) {
        // The button was released outside the window: finish as a release.
        if (d.edit !== null) {
          const reason = d.edit.commit();
          if (reason !== null) services.ui.toast(reason, { kind: 'error' });
        }
        finish();
        return;
      }
      d.pointer = [e.px, e.py];
      if (!d.moved) {
        if (Math.hypot(e.px - d.startPx[0], e.py - d.startPx[1]) < dp(c, DRAG_START)) return;
        if (!begin(c, d, e)) {
          finish();
          return;
        }
        d.moved = true;
      }
      apply(c, d, e);
    },
    onPointerUp(c, e) {
      const d = drag;
      if (d === null) return;
      if (!d.moved || d.edit === null) {
        finish();
        // A click on the active line without dragging: the same as any line click (cycling).
        if (d.handle === 'moveBody') clickAt(services, c, { ...e, px: d.startPx[0], py: d.startPx[1], buttons: 1 });
        return;
      }
      const reason = d.edit.commit();
      if (reason !== null) services.ui.toast(reason, { kind: 'error' });
      finish();
    },
  };
}
